/**
 * Task 280B — derive AI content-calendar generation defaults from the client's
 * active package config + operating setup, and a merge helper that applies them
 * to the form ONLY where the user has not overridden the initial default.
 *
 * Pure (no React, no network) so it is unit-tested directly. FE-only: the
 * package monthly counts come from the active assignment's LIVE `services[].config`
 * (the assignment snapshot does not carry config), and scheduling defaults from
 * `client_operating_setup`. Nothing here calls an API or mutates anything.
 *
 * `entry_count` is a per-MONTH item count for the generator; stories are volume
 * only and never inflate it (see `monthlyContentEntryCount`).
 */

import type { SocialServiceConfig } from '@kmi/contracts';

import type {
  ClientOperatingSetup,
  ClientPackageAssignmentRow,
  ContentVariantPlatform,
} from '../../api/types';
import {
  normalizeConfig,
  resolveKind,
} from '../../components/services-packages/packageServiceConfigModel';
import {
  clampMonthlyCount,
  monthlyContentEntryCount,
} from '../../components/services-packages/socialWeeklyHint';
import { dayLabel } from './operatingSetup';

// Mirror the EF's request limits (generate-content-calendar-draft) so prefill
// never produces an out-of-range value.
export const ENTRY_COUNT_MIN = 1;
export const ENTRY_COUNT_MAX = 30;
export const PLATFORMS_MAX = 4;

/**
 * Task 280F/1D — why `entryCount` ended up derived vs falling back to the form
 * default. Additive/diagnostic only; it does NOT change `entryCount` behavior.
 *   - `derived`            → a real monthly count was derived from the package.
 *   - `no_assignment`      → the client has no active package assignment.
 *   - `no_included_social` → an assignment exists but no INCLUDED social service.
 *   - `no_monthly`         → an included social service exists but its monthly
 *                            designs+videos are 0 (stories don't count as slots).
 */
export type EntryCountReason = 'derived' | 'no_assignment' | 'no_included_social' | 'no_monthly';

export interface ContentCalendarPrefill {
  /** Monthly item count; null = no package signal, keep the current default. */
  readonly entryCount: number | null;
  /** Diagnostic: why entryCount is derived vs null (UI explainability only). */
  readonly entryCountReason: EntryCountReason;
  readonly platforms: ContentVariantPlatform[] | null;
  readonly timezone: string | null;
  /** YYYY-MM floor; null = keep the current default month. */
  readonly month: string | null;
  /** Advisory instruction block; null = keep the current (empty) instruction. */
  readonly instruction: string | null;
}

/** The subset of the AI form prefill seeds — used by the merge helper. */
export interface ContentCalendarFormDefaults {
  readonly entryCount: number;
  readonly platforms: ReadonlyArray<ContentVariantPlatform>;
  readonly timezone: string;
  readonly month: string;
  readonly instruction: string;
}

interface MonthlyAggregate {
  readonly feed: number;
  readonly reelsVideos: number;
  readonly stories: number;
  readonly entryCount: number;
  /** Count of INCLUDED social services seen (for the no_included_social vs no_monthly reason). */
  readonly includedSocialCount: number;
}

/** Sum the monthly feed / reels-videos / stories across the active package's included social services. */
function monthlyFromAssignment(assignment: ClientPackageAssignmentRow | null): MonthlyAggregate {
  let feed = 0;
  let reelsVideos = 0;
  let stories = 0;
  let entryCount = 0;
  let includedSocialCount = 0;
  if (!assignment) return { feed, reelsVideos, stories, entryCount, includedSocialCount };
  for (const svc of assignment.services) {
    if (svc.included === false) continue;
    const kind = resolveKind(svc.config, svc.system_capability_key, svc.service_name);
    if (kind !== 'social') continue;
    includedSocialCount += 1;
    const cfg = normalizeConfig('social', svc.config) as SocialServiceConfig;
    feed += clampMonthlyCount(cfg.monthly.designs);
    reelsVideos += clampMonthlyCount(cfg.monthly.videos);
    stories += clampMonthlyCount(cfg.monthly.stories);
    entryCount += monthlyContentEntryCount(cfg.monthly);
  }
  return { feed, reelsVideos, stories, entryCount, includedSocialCount };
}

/** Advisory earliest publish date = first_publish_date + production lead + approval buffer (YYYY-MM-DD), or null. */
function earliestPublishDate(setup: ClientOperatingSetup | null): string | null {
  if (!setup || !setup.first_publish_date) return null;
  const ms = Date.parse(`${setup.first_publish_date}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  const offsetDays =
    clampMonthlyCount(setup.production_lead_time_days) +
    clampMonthlyCount(setup.approval_buffer_days);
  const d = new Date(ms + offsetDays * 86_400_000);
  return d.toISOString().slice(0, 10);
}

function buildAdvisory(
  agg: MonthlyAggregate,
  setup: ClientOperatingSetup | null,
  earliest: string | null,
): string | null {
  const lines: string[] = [];
  if (agg.feed > 0 || agg.reelsVideos > 0) {
    lines.push(
      `- المزيج الشهري المقترح من الباقة: ~${agg.feed} منشور/تصميم و~${agg.reelsVideos} ريلز/فيديو.`,
    );
  }
  if (agg.stories > 0) {
    lines.push(
      `- الستوري (~${agg.stories}/شهر) حجم فقط ولا تُحتسب ضمن عدد خانات التقويم (المنشورات/الريلز).`,
    );
  }
  if (earliest) {
    lines.push(`- أبكر تاريخ نشر مقترح: ${earliest}.`);
  }
  const days = (setup?.preferred_days ?? []).map(dayLabel).join('، ');
  const times = (setup?.preferred_times_local ?? []).join('، ');
  if (days.length > 0 || times.length > 0) {
    const tz = setup?.timezone ? ` (${setup.timezone})` : '';
    const parts = [
      days.length > 0 ? `الأيام المفضّلة: ${days}` : '',
      times.length > 0 ? `الأوقات: ${times}${tz}` : '',
    ].filter((p) => p.length > 0);
    lines.push(`- ${parts.join('؛ ')}.`);
  }
  if (lines.length === 0) return null;
  return ['سياق التشغيل (استرشادي، غير مُلزِم):', ...lines].join('\n');
}

/**
 * Dedupe the operating-setup platforms, keep only those the caller's surface can
 * actually render (the AI generator allow-list), and cap at the API max. This is
 * critical: seeding a platform the picker doesn't render (e.g. Facebook, which the
 * AI generator excludes) would be invisible and unremovable — breaking the
 * manual-override guarantee.
 */
function pickAllowedPlatforms(
  platforms: ReadonlyArray<ContentVariantPlatform>,
  allowed: ReadonlyArray<ContentVariantPlatform>,
): ContentVariantPlatform[] {
  const allowedSet = new Set(allowed);
  return [...new Set(platforms)].filter((p) => allowedSet.has(p)).slice(0, PLATFORMS_MAX);
}

/**
 * Derive the AI generation prefill from the active package assignment + operating
 * setup. Every field is nullable; null means "no signal — keep the current default".
 * `allowedPlatforms` is the consuming surface's renderable platform set; seeded
 * platforms are filtered to it so the user can always see/remove them.
 */
export function deriveContentCalendarPrefill(
  assignment: ClientPackageAssignmentRow | null,
  setup: ClientOperatingSetup | null,
  opts: {
    readonly defaultMonth: string;
    readonly allowedPlatforms: ReadonlyArray<ContentVariantPlatform>;
  },
): ContentCalendarPrefill {
  const agg = monthlyFromAssignment(assignment);
  const earliest = earliestPublishDate(setup);
  const earliestMonth = earliest ? earliest.slice(0, 7) : null;
  const picked =
    setup?.platforms && setup.platforms.length > 0
      ? pickAllowedPlatforms(setup.platforms, opts.allowedPlatforms)
      : [];

  // Diagnostic reason (UI explainability only — does not affect entryCount).
  const entryCountReason: EntryCountReason =
    assignment === null
      ? 'no_assignment'
      : agg.entryCount >= ENTRY_COUNT_MIN
        ? 'derived'
        : agg.includedSocialCount === 0
          ? 'no_included_social'
          : 'no_monthly';

  return {
    entryCount:
      agg.entryCount >= ENTRY_COUNT_MIN ? Math.min(ENTRY_COUNT_MAX, agg.entryCount) : null,
    entryCountReason,
    platforms: picked.length > 0 ? picked : null,
    timezone: setup?.timezone && setup.timezone.trim().length > 0 ? setup.timezone : null,
    // YYYY-MM is lexicographically ordered, so a plain string compare is chronological.
    month: earliestMonth && earliestMonth > opts.defaultMonth ? earliestMonth : null,
    instruction: buildAdvisory(agg, setup, earliest),
  };
}

/**
 * Apply a prefill to the current form, field-by-field, ONLY where the form still
 * holds the initial default (so a user's manual edit always wins). One-time
 * seeding — call once after the package/operating-setup data is available.
 */
export function mergeContentCalendarPrefill<T extends ContentCalendarFormDefaults>(
  prev: T,
  prefill: ContentCalendarPrefill,
  initial: ContentCalendarFormDefaults,
): T {
  return {
    ...prev,
    entryCount:
      prev.entryCount === initial.entryCount && prefill.entryCount !== null
        ? prefill.entryCount
        : prev.entryCount,
    platforms:
      prev.platforms === initial.platforms && prefill.platforms !== null
        ? prefill.platforms
        : prev.platforms,
    timezone:
      prev.timezone === initial.timezone && prefill.timezone !== null
        ? prefill.timezone
        : prev.timezone,
    month: prev.month === initial.month && prefill.month !== null ? prefill.month : prev.month,
    instruction:
      prev.instruction === initial.instruction && prefill.instruction !== null
        ? prefill.instruction
        : prev.instruction,
  };
}
